import { ClickAway } from '@components/core-ui/ClickAway';
import { useRef, useState } from 'react';
import { Scrollbars } from 'react-custom-scrollbars-2';

import { IconButtonClean } from '@components/core-ui/form-elements/buttons/IconButton/variations/IconButtonClean';
import { FormFieldIcon } from '@components/core-ui/form-elements/FormFieldIcon';
import { FormFieldRoot } from '@components/core-ui/form-elements/FormFieldRoot';
import { FormFieldSelectLabel } from '@components/core-ui/form-elements/FormFieldSelectLabel';
import { FormFieldSelectOption } from '@components/core-ui/form-elements/FormFieldSelectOption';
import { FormFieldSelectOptionsPanel } from '@components/core-ui/form-elements/FormFieldSelectOptionsPanel';
import ClosedIcon from '@components/icons/ClosedChevron.svg';
import OpenedIcon from '@components/icons/ChevronOpened.svg';
import { TProps } from './types/TProps';

const selectWrapperClassName = 'w-full [&>div]:relative';

const selectCompactWrapperClassName = 'shrink-0 [&>div]:relative';

const selectOptionsLabelClassName = [
  'optionsLabel relative mx-[15px] flex h-10 cursor-pointer items-center',
  'border-b border-[var(--color-form-field-select-divider,var(--color-border))]',
].join(' ');

const selectOptionsClassName = 'options block';

const selectIconClosedClassName = 'icon--closed right-5 h-auto w-auto cursor-pointer';

const selectIconOpenedClassName = 'icon--opened right-[5px] h-auto w-auto cursor-pointer';

export const Select = ({
  name = 'select',
  options,
  label,
  onClick,
  onChange,
  value,
  className = 'form-select',
  hideSelectedOption = false,
  required = false,
  disabled = false,
  error = false,
  testId,
  triggerClassName,
  iconClassName,
  labelClassName,
  optionsWrapperClassName,
  compact = false,
  placement = 'bottom-start',
}: TProps) => {
  const [optionsVisible, setOptionsVisible] = useState(false);
  const triggerRef = useRef<HTMLSpanElement>(null);

  const toggleOptions = () => {
    if (disabled) {
      return;
    }
    setOptionsVisible(!optionsVisible);
  };

  const closeOptions = () => {
    setOptionsVisible(false);
  };

  const selectOptions = options
    .filter((option) => (hideSelectedOption ? option.value !== value : true))
    .map((option, k) => {
      const onClick = (event: React.MouseEvent<HTMLSpanElement>) => {
        onChange(name, `${option.value}`, event);
        setOptionsVisible(!optionsVisible);
      };
      const optionTestId = testId ? `${testId}-option-${option.label.replace(/\s+/g, '-')}` : undefined;
      return (
        <FormFieldSelectOption
          onClick={onClick}
          selected={value === option.value}
          key={k}
          data-testid={optionTestId}
          role="option"
        >
          {option.label}
        </FormFieldSelectOption>
      );
    });

  const selectedOption = options.find((option) => option.value === value);

  const getCurrentLabel = () => {
    if (selectedOption) {
      return selectedOption.label;
    }
    return `${label}${required ? ' *' : ''}`;
  };

  return (
    <FormFieldRoot
      disabled={disabled}
      className={[compact ? selectCompactWrapperClassName : selectWrapperClassName, className]
        .filter(Boolean)
        .join(' ')}
    >
      <ClickAway onClickAway={closeOptions}>
        <span
          ref={triggerRef}
          className={[
            'select select-control',
            disabled ? 'select-control--disabled' : '',
            error ? 'select-control--error' : '',
            triggerClassName,
          ]
            .filter(Boolean)
            .join(' ')}
          onClick={onClick || toggleOptions}
          data-testid={testId}
          role="combobox"
          aria-expanded={optionsVisible}
          aria-haspopup="listbox"
          aria-disabled={disabled}
        >
          {compact ? (
            <button
              type="button"
              aria-label={optionsVisible ? 'Close options' : 'Open options'}
              className={iconClassName}
              onClick={(e) => {
                e.stopPropagation();
                toggleOptions();
              }}
            >
              <ClosedIcon />
            </button>
          ) : (
            <FormFieldIcon clickable className={[selectIconClosedClassName, iconClassName].filter(Boolean).join(' ')}>
              <IconButtonClean
                type="button"
                aria-label={optionsVisible ? 'Close options' : 'Open options'}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleOptions();
                }}
              >
                <ClosedIcon />
              </IconButtonClean>
            </FormFieldIcon>
          )}
          <FormFieldSelectLabel selected={Boolean(selectedOption)} disabled={disabled} className={labelClassName}>
            {getCurrentLabel()}
          </FormFieldSelectLabel>
        </span>

        {optionsVisible ? (
          <FormFieldSelectOptionsPanel
            triggerRef={triggerRef}
            placement={placement}
            className={optionsWrapperClassName}
            data-testid={testId ? `${testId}-options` : undefined}
            role="listbox"
          >
            <Scrollbars autoHeight autoHeightMin={24} autoHeightMax={150}>
              <span className={selectOptionsLabelClassName} onClick={toggleOptions}>
                {getCurrentLabel()}
                <FormFieldIcon clickable className={selectIconOpenedClassName}>
                  <IconButtonClean
                    type="button"
                    aria-label="Close options"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleOptions();
                    }}
                  >
                    <OpenedIcon />
                  </IconButtonClean>
                </FormFieldIcon>
              </span>
              <span className={selectOptionsClassName}>{selectOptions}</span>
            </Scrollbars>
          </FormFieldSelectOptionsPanel>
        ) : null}
      </ClickAway>
    </FormFieldRoot>
  );
};
